import { auditRubricToSchema } from '@/client/services/sessionAudit/internal/helpers'

import { COACH_ESCALATION_SLUG, INTERNAL_COMMENT_SLUG, SAFETY_REPORT_SLUG } from '../constants'
import type {
  AuditRubricQuestions,
  AuditRubricResponseLevel,
  AuditRubricResponseYesNo,
  AuditRubricVersion,
} from '../types'

const ANSWER_MIN_LENGTH = 20

const DEFAULT_LEVEL: AuditRubricResponseLevel = 'level-0'
const DEFAULT_YES_NO: AuditRubricResponseYesNo = 'no'
const DEFAULT_SHORT_ANSWER = '[Default Comment]'

const AUDIT_RUBRIC_LEAD_V1: AuditRubricQuestions = {
  version: 'lead-v1',
  blocks: [
    // Lay the Foundation
    {
      slug: 'lay-the-foundation',
      title: 'Lay the Foundation',
      questions: [
        {
          slug: 'lay-the-foundation-1',
          type: 'level',
          required: true,
          label: 'Tutor is prepared to run the session smoothly',
          description:
            'Was the session able to start and progress without major interruptions? Did the tutor appear organized and ready—both technically and with the content?',
          responseDescriptions: {
            'level-1': 'Technical, logistical or curriculum issues caused significant disruption.',
            'level-2': 'Technical, logistical or curriculum issues caused some disruption.',
            'level-3': 'Session ran well, without major hiccups.',
            'level-4':
              'Session ran very smoothly, with careful attention to detail from the tutor.',
            'level-0': 'Not applicable.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'lay-the-foundation-2',
          type: 'level',
          required: true,
          label: 'Conveyed a deep understanding of material',
          description:
            'Does the tutor go beyond sharing surface-level process and help the learner grasp the underlying concepts in a deep, intuitive and generalizable way?',
          responseDescriptions: {
            'level-1': 'Gets questions wrong, gives out disinformation.',
            'level-2':
              'Explains the process to solve a problem correctly, but does not help the learner understand the underlying concepts: why does this process work the way that it does?',
            'level-3':
              'Explains the process to solve a problem correctly, and also explains the underlying concepts in a reasonable way. Most questions they ask learners are confirming that they understand how to complete the problem, not about why the solution is what it is.',
            'level-4':
              'Explains the process to solve a problem correctly, and also gives an excellent explanation of underlying concepts through appropriate analogies, visuals and examples. Instead of only asking learners questions about how to solve a problem, they engage them in discussing why the solution is what it is.',
            'level-0':
              'Learner already understands underlying concepts, and only needs procedural help or session was an atypical format such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'lay-the-foundation-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Consider the explanations, visuals, analogies and examples that the tutor used to cover the content of this session—what worked well? What could be improved?',
          sublabel:
            'Please focus on content-specific examples! This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Engage Actively
    {
      slug: 'engage-actively',
      title: 'Engage Actively',
      questions: [
        {
          required: true,
          type: 'level',
          slug: 'engage-actively-1',
          label: 'Continuously engages learners',
          description:
            'Is the tutor able to effectively engage learners as active participants, and keep their attention? Is the tutor speaking less than the learner(s) are? Please note that some interactions may happen over private Zoom DMs, but try your best to identify this from context.',
          responseDescriptions: {
            'level-1':
              'If learners participate, it is only to share answers to practice problems or answer yes/no questions.',
            'level-2':
              'Provides occasional chances for learners to engage with material, ask questions, and solve problems. However, nearly all of the actual engagement is coming from the tutor.',
            'level-3':
              'Frequently engages learners in solving problems / working on activities, and discussing the content. However, most of the actual engagement and discussion still comes from the tutor.',
            'level-4':
              'Engages learners as leaders and owners of the session; they take the lead in solving problems, with the tutor functioning only as support. The learners are as engaged and vocal as the tutor, if not more. It would be impossible for a learner to multitask while participating in this session, without the tutor noticing.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'level',
          slug: 'engage-actively-2',
          label: 'Encouraged learners to explain their thinking',
          description:
            'Did the tutor ask learners to explain how they arrived at an answer—regardless of whether it was correct? Did they use those explanations to deepen understanding or guide next steps?',
          responseDescriptions: {
            'level-1':
              'Tutor primarily checks for right or wrong answers without exploring how learners arrived at their responses. Rarely asks follow-up questions.',
            'level-2':
              'Tutor occasionally asks learners to explain their thinking, but it feels inconsistent or mostly limited to correct answers.',
            'level-3':
              'Tutor regularly prompts learners to walk through their thinking, even when they’re unsure or incorrect, and uses these moments to guide learning.',
            'level-4':
              'Skillfully invites learners to explain their reasoning, responds with meaningful follow-up questions, and uses learner input to shape pacing, examples, or re-teaching.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'short-answer',
          slug: 'engage-actively-comment-1',
          label: 'Please justify your ratings above—what worked well? What could be improved?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Adjust as Needed
    {
      slug: 'adjust-as-needed',
      title: 'Adjust as Needed',
      questions: [
        {
          slug: 'adjust-as-needed-1',
          type: 'level',
          required: true,
          label: 'Seeks to understand learners who exhibit signs of struggle',
          description:
            'Did the tutor notice when a learner might be struggling—through hesitation, silence, confusion, or disengagement? Did they try to understand what was going on beneath the surface (e.g., confusion, anxiety, frustration), rather than ignoring or misinterpreting it?',
          responseDescriptions: {
            'level-1':
              'Tutor misses or overlooks potential signs that a learner is struggling (e.g., silence, repeated errors, disengagement) and continues without acknowledging them.',
            'level-2':
              'Tutor occasionally notices visible signs of struggle but may misread them or hesitate to follow up. Support feels reactive or incomplete.',
            'level-3':
              'Tutor identifies several clear signs of struggle during the session and makes an effort to check in, clarify, or offer support. Begins to recognize patterns across learners.',
            'level-4':
              'Tutor is highly attuned to subtle signs of struggle—such as changes in tone, pacing, or body language—and actively seeks to understand what’s beneath the surface. Creates space for learners to express confusion without judgment.',
            'level-0':
              'The learner clearly communicated their own areas of struggle (e.g., asked direct questions, named their confusion), or the session followed an atypical format where signs of struggle were unlikely to surface (e.g., a test-taking session).',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'adjust-as-needed-2',
          type: 'level',
          required: true,
          label: 'Adapts to learners’ unique needs',
          description:
            'Once a learner’s need or challenge is identified, did the tutor adjust their instruction, pacing, or strategy to better support them? Did the tutor shift their approach in response to how the group or individual learners were engaging with the material?',
          responseDescriptions: {
            'level-1':
              'Tutor generally does not adapt their tutoring based on how learners respond. When learners get questions wrong, they may repeat their original explanation of the concept but continue to power through.',
            'level-2':
              "Asks learners questions to see if they're following, but most questions are not very effective (e.g., “does that make sense”?). For most of the session, it's unclear whether the learners are following along.",
            'level-3':
              'Asks some effective questions to check which learners are following along, has learner(s) explain their reasoning, and helps fill in knowledge gaps they find. However, there are still some points in the session where the tutor struggles to be able to adapt to learner(s) needs.',
            'level-4':
              "Engages learners in a way that makes it very clear when they're not following along. Builds off of learners' existing knowledge in a highly successful way.",
            'level-0':
              "Session is very large or, for some other reason, it doesn't make sense for it to be adaptive",
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'short-answer',
          slug: 'adjust-as-needed-comment-1',
          label: 'Please justify your ratings above—what worked well? What could be improved?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Develop Connections
    {
      slug: 'develop-connections',
      title: 'Develop Connections',
      questions: [
        {
          slug: 'develop-connections-1',
          type: 'level',
          required: true,
          label: 'Strengthens learners’ confidence',
          description:
            "Does the tutor subtly strengthen the learner's belief that they're capable of learning the content? Do they show patience and empathy when a learner makes a mistake, and turn mistakes into learning opportunities?",
          responseDescriptions: {
            'level-0': 'Atypical session format, such as a test-taking session',
            'level-1':
              "Grows frustrated or impatient when learners make mistakes, or questions the learner's capability of learning the content.",
            'level-2':
              'Does their best to be patient when the learner makes mistakes, but does not know how to guide them through in a way that avoids discouragement. Words of encouragement come across as mostly empty, given the context.',
            'level-3':
              "Able to skillfully navigate learner's mistakes, guiding them in the right direction and keeping them focused on progress. They use words of encouragement throughout the session in an appropriate way.",
            'level-4':
              'Builds an environment where learners are very comfortable making mistakes and learning from them. The tutor is tuned in to how the learners are feeling and finds small but effective ways to develop their confidence in their capability to master the material.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'develop-connections-2',
          type: 'level',
          required: true,
          label: 'Creates a comfortable learning environment',
          description:
            'Does the tutor greet each learner by name and take the time to show that they care? Do they show sensitivity towards differences across the group, such as timezones, language experience, cultural background, or connectivity difficulties?',
          responseDescriptions: {
            'level-1':
              "May say hello, but does not use anyone's names or introduce themselves. The tutor may show insensitivity towards a learner's background or situation.",
            'level-2':
              "Greets each learner as they join, but doesn't facilitate conversation and doesn't continue to refer to each learner by name. The tutor tries to be sensitive of learners, but needs some support to know how to do so (e.g., assuming name pronunciations and getting them wrong).",
            'level-3':
              'Shows respect towards all learner(s), referring to them by name throughout the session, and presenting themselves as a peer who cares about their success. There is some evidence of learner-to-learner interaction that the tutor tries to encourage.',
            'level-4':
              'Facilitates a deeply friendly and respectful environment, where tutor and learner(s) refer to each other by name, show their personality, and feel a sense of community. The tutor actively encourages and facilitates learner-to-learner interaction.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'short-answer',
          slug: 'develop-connections-comment-1',
          label: 'Please justify your ratings above—what worked well? What could be improved?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Safety
    {
      slug: 'safety',
      title: 'Safe & Appropriate Environment',
      questions: [
        {
          slug: SAFETY_REPORT_SLUG,
          type: 'yes-no',
          required: true,
          label: 'Keeps interactions safe and appropriate',
          description: `Does the tutor facilitate a safe environment for learners, in accordance with Schoolhouse's community guidelines? Were there any interactions that seemed concerning?`,
          responseDescriptions: {
            yes: 'All interactions were safe and appropriate.',
            no: 'Something about the session was questionable, and should be escalated to the safety team.',
          },
          defaultValue: 'yes',
        },
        {
          slug: 'safety-2',
          type: 'yes-no',
          required: true,
          label: `Never completes the learner's homework for them`,
          description: '',
          responseDescriptions: {
            yes: 'The tutor guided the learner, but the learner did all the thinking and work.',
            no: 'The tutor solved problems or completed steps for the learner.',
          },
          defaultValue: 'yes',
        },
      ],
    },
    // Internal Escalation
    {
      slug: 'internal',
      title: 'Session Escalation',
      questions: [
        {
          slug: COACH_ESCALATION_SLUG,
          type: 'yes-no',
          required: true,
          label: 'Escalate this session to a coach for further review?',
          description:
            'Do you think the feedback you provided should be enough to help the tutor, or would it be best for there to be more intervention?',
          responseDescriptions: {
            yes: 'There was at least one issue in this session that I think the tutor needs additional support to work through.',
            no: 'The feedback I shared should be enough to help the tutor improve next time.',
          },
          defaultValue: DEFAULT_YES_NO,
        },
        {
          slug: INTERNAL_COMMENT_SLUG,
          type: 'short-answer',
          required: false,
          label:
            "It looks like you've escalated this session because of safety or quality concerns. " +
            'Please share your specific concerns so our team can look into them further—this note will not be shared with the tutor!',
          sublabel: 'Only coaches and safety team members will be able to see your comment.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
  ],
}

const AUDIT_RUBRIC_MARS_V2: AuditRubricQuestions = {
  version: 'mars-v2',
  blocks: [
    // Mastery
    {
      slug: 'mastery',
      title: 'Mastery',
      questions: [
        {
          slug: 'mastery-1',
          type: 'level',
          required: true,
          label: 'Conveys a deep understanding of material',
          description:
            'Does the tutor go beyond sharing surface-level process and help the learner grasp the underlying concepts in a deep, intuitive and generalizable way?',
          responseDescriptions: {
            'level-1': 'Gets questions wrong, gives out disinformation.',
            'level-2':
              'Explains the process to solve a problem correctly, but does not help the learner understand the underlying concepts: why does this process work the way that it does?',
            'level-3':
              'Explains the process to solve a problem correctly, and also explains the underlying concepts in a reasonable way. Most questions they ask learners are confirming that they understand how to complete the problem, not about why the solution is what it is.',
            'level-4':
              'Explains the process to solve a problem correctly, and also gives an excellent explanation of underlying concepts through appropriate analogies, visuals and examples. Instead of only asking learners questions about how to solve a problem, they engage them in discussing why the solution is what it is.',
            'level-0':
              'Learner already understands underlying concepts, and only needs procedural help',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'mastery-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Consider the explanations, visuals, analogies and examples that the tutor used to cover the content of this session—what worked well? What could be improved?',
          sublabel:
            'Please focus on content-specific examples! This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    {
      // Active Learning
      slug: 'active',
      title: 'Active Learning',
      questions: [
        {
          slug: 'active-1',
          type: 'level',
          required: true,
          label: `Checks learners' prior knowledge`,
          description: `Is the tutor able to effectively evaluate the learner's existing understanding, and quickly assess which knowledge gaps need to be filled? This may include going back to review previous units.`,
          responseDescriptions: {
            'level-1': `Does not check the learner's prior knowledge.`,
            'level-2':
              'Asks learners about their existing understanding or starts with warm-up problem(s), but fails to use that knowledge to tailor the session.',
            'level-3': `Starts session with an appropriate method to check the learner's understanding, and uses this to tailor the session to a limited extent.`,
            'level-4': `Starts session with an appropriate method to check the learner's understanding, and then uses this to guide the pace and direction of the session — filling in knowledge gaps before moving forward.`,
            'level-0': `Series session where there's evidence that the tutor has already checked prior knowledge`,
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-2',
          type: 'level',
          required: true,
          label: `Adapted to learners' unique needs`,
          description:
            'Does the tutor consistently notice when a learner was not following, and take the time to understand why? Do they spend the time to hone in on those knowledge gaps and rebuild their understanding, before moving on?',
          responseDescriptions: {
            'level-1':
              'Generally does not adapt their tutoring based on how learners respond. When learners get questions wrong, they may repeat their original explanation of the concept but continue to power through.',
            'level-2': `Asks learners questions to see if they're following, but most questions are not very effective (e.g., “does that make sense”?). For most of the session, it's unclear whether the learners are following along.`,
            'level-3':
              'Asks some effective questions to check which learners are following along, has learner(s) explain their reasoning, and helps fill in knowledge gaps they find. However, there are still some points in the session where the tutor struggles to be able to adapt to learner(s) needs.',
            'level-4': `Engages learners in a way that makes it very clear when they're not following along. Builds off of learners' existing knowledge in a highly successful way.`,
            'level-0': `Session is very large or, for some other reason, it doesn't make sense for it to be adaptive`,
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-3',
          type: 'level',
          required: true,
          label: 'Continuously engages learners',
          description: `Is the tutor able to effectively engage learners as active participants, and keep their attention? Is the tutor speaking less than the learner(s) are? Please note that some interactions may happen over private Zoom DM's, but try your best to identify this from context.`,
          responseDescriptions: {
            'level-1':
              'If learners participate, it is only to share answers to practice problems or answer yes/no questions.',
            'level-2':
              'Provides occasional chances for learners to engage with material, ask questions, and solve problems. However, nearly all of the actual engagement is coming from the tutor.',
            'level-3':
              'Frequently engages learners in solving problems / working on activities, and discussing the content. However, most of the actual engagement and discussion still comes from the tutor.',
            'level-4':
              'Engages learners as leaders and owners of the session; they take the lead in solving problems, with the tutor functioning only as support. The learners are as engaged and vocal as the tutor, if not more. It would be impossible for a learner to multitask while participating in this session, without the tutor noticing.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-4',
          type: 'level',
          required: true,
          label: `Confirms learners' progress`,
          description:
            'In the end, is there evidence that the learner increased their understanding?',
          responseDescriptions: {
            'level-1': 'Does not attempt to understand if the learner made progress.',
            'level-2': `Gets a sense of the learner's progress by working out problems together, but the learner does not complete anything on their own. It's unclear who did and didn't make progress.`,
            'level-3':
              'As the session is coming to a close, the tutor has learners complete practice problems (or some other activity) that shows whether or not they made progress. Little follow-up, beyond encouraging words.',
            'level-4':
              'As the session is coming to a close, the tutor has learners complete practice problems (or some other activity) that objectively shows whether or not they made progress. They also discuss the progress with learners afterwards, and ensure that each one knows what their next steps for progressing through the content.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Please justify your ratings above—what worked well? What could be improved? This will be shared with the tutor.',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
        {
          slug: 'active-comment-2',
          type: 'short-answer',
          required: true,
          label: 'Is there any evidence that the learners made progress?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Respect
    {
      slug: 'respect',
      title: 'Respectful Community',
      questions: [
        {
          slug: 'respect-1',
          type: 'level',
          required: true,
          label: `Strengthens learners' confidence`,
          description: `Does the tutor subtly strengthen the learner's belief that they're capable of learning the content? Do they show patience and empathy when a learner makes a mistake, and turn mistakes into learning opportunities?`,
          responseDescriptions: {
            'level-1': `Grows frustrated or impatient when learners make mistakes, or questions the learner's capability of learning the content.`,
            'level-2':
              'Does their best to be patient when the learner makes mistakes, but does not know how to guide them through in a way that avoids discouragement. Words of encouragement come across as mostly empty, given the context.',
            'level-3': `Able to skillfully navigate learner's mistakes, guiding them in the right direction and keeping them focused on progress. They use words of encouragement throughout the session in an appropriate way.`,
            'level-4':
              'Builds an environment where learners are very comfortable making mistakes and learning from them. The tutor is tuned in to how the learners are feeling and finds small but effective ways to develop their confidence in their capability to master the material.',
            'level-0': 'Tutor did not engage learners in practice problems',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'respect-2',
          type: 'level',
          required: true,
          label: 'Creates a comfortable learning environment',
          description:
            'Does the tutor greet each learner by name and take the time to show that they care? Do they show sensitivity towards differences across the group, such as timezones, language experience, cultural background, or connectivity difficulties?',
          responseDescriptions: {
            'level-1': `May say hello, but does not use anyone's names or introduce themselves. The tutor may show insensitivity towards a learner's background or situation.`,
            'level-2': `Greets each learner as they join, but doesn't facilitate conversation and doesn't continue to refer to each learner by name. The tutor tries to be sensitive of learners, but needs some support to know how to do so (e.g., assuming name pronunciations and getting them wrong).`,
            'level-3':
              'Shows respect towards all learner(s), referring to them by name throughout the session, and presenting themselves as a peer who cares about their success.',
            'level-4':
              'Facilitates a deeply friendly and respectful environment, where tutor and learner(s) refer to each other by name, show their personality, and feel a sense of community.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'respect-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Please justify your ratings above—what worked well? What could be improved? This will be shared with the tutor.',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Safety
    {
      slug: 'safety',
      title: 'Safe & Appropriate Environment',
      questions: [
        {
          slug: SAFETY_REPORT_SLUG,
          type: 'yes-no',
          required: true,
          label: 'Keeps interactions safe and appropriate',
          description: `Does the tutor facilitate a safe environment for learners, in accordance with Schoolhouse's community guidelines? Were there any interactions that seemed concerning?`,
          responseDescriptions: {
            yes: 'All interactions were safe and appropriate.',
            no: 'Something about the session was questionable, and should be escalated to the safety team.',
          },
          defaultValue: 'yes',
        },
        {
          slug: 'safety-2',
          type: 'yes-no',
          required: true,
          label: `Never completes the learner's homework for them`,
          description: '',
          responseDescriptions: {
            yes: 'The tutor guided the learner, but the learner did all the thinking and work.',
            no: 'The tutor solved problems or completed steps for the learner.',
          },
          defaultValue: 'yes',
        },
      ],
    },
    // Internal Escalation
    {
      slug: 'internal',
      title: 'Session Escalation',
      questions: [
        {
          slug: COACH_ESCALATION_SLUG,
          type: 'yes-no',
          required: true,
          label: 'Escalate this session to a coach for further review?',
          description:
            'Do you think the feedback you provided should be enough to help the tutor, or would it be best for there to be more intervention?',
          responseDescriptions: {
            yes: 'There was at least one issue in this session that I think the tutor needs additional support to work through.',
            no: 'The feedback I shared should be enough to help the tutor improve next time.',
          },
          defaultValue: DEFAULT_YES_NO,
        },
        {
          slug: INTERNAL_COMMENT_SLUG,
          type: 'short-answer',
          required: false,
          label:
            "It looks like you've escalated this session because of safety or quality concerns. " +
            'Please share your specific concerns so our team can look into them further—this note will not be shared with the tutor!',
          sublabel: 'Only coaches and safety team members will be able to see your comment.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
  ],
}

const AUDIT_RUBRIC_MARS_SAT_V3: AuditRubricQuestions = {
  version: 'mars-sat-v3',
  blocks: [
    // Mastery
    {
      slug: 'mastery',
      title: 'Mastery',
      questions: [
        {
          slug: 'mastery-1',
          type: 'level',
          required: true,
          label: 'Well-prepared to teach content',
          description:
            'Has the tutor done the necessary preparation for this session? Is the tutor prepared to answer questions in a clear and thorough way?',
          responseDescriptions: {
            'level-1':
              'Hasn’t prepared for session, gets multiple questions wrong, gives out disinformation.',
            'level-2':
              'Tutor struggles to explain material, perhaps failing to review the material sufficiently beforehand. However, explanations are mostly correct.',
            'level-3':
              'Explains the process to answer a question correctly, in a reasonable way. They also help learners understand how to approach the questions during a real test.',
            'level-4':
              'Explains how to solve problems not only correctly, but in a very clear and intuitive way. They also share excellent strategies for facing similar questions during a real test.',
            'level-0': 'No test content was reviewed during the session.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'mastery-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Was the tutor well prepared for the session, providing effective explanations, visuals and examples? What worked well? What could be improved?',
          sublabel:
            'Please focus on content-specific examples! This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Active Learning
    {
      slug: 'active',
      title: 'Active Learning',
      questions: [
        {
          slug: 'active-1',
          type: 'level',
          required: true,
          label: 'Engages learners to participate during “I do” strategy',
          description:
            'Does the tutor effectively engage learners while demonstrating how to complete problems?',
          responseDescriptions: {
            'level-1': 'Does not engage learners while demonstrating concepts.',
            'level-2': 'Occasionally checks in to see if learners have questions.',
            'level-3':
              'Asks learners about their pre-requisite knowledge, solicits learner questions and checks in regularly to make sure learners are following along.',
            'level-4':
              'Asks learners about their pre-requisite knowledge, solicits learner questions and checks in regularly to make sure learners are following along. Uses language that normalizes asking questions, and gives learners sufficient time to respond.',
            'level-0': 'N/A, tutor did not demonstrate how to solve problems during this session.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-2',
          type: 'level',
          required: true,
          label: 'Effectively engages all learners to lead problem-solving during “we do” strategy',
          description:
            'Does the tutor effectively encourage learners to share their ideas with the group?',
          responseDescriptions: {
            'level-1': 'Does not ask learners to take the lead in solving any problems.',
            'level-2':
              'Asks learners to lead problem-solving, but often takes over and explains most things in their own words.',
            'level-3':
              'Has learners lead problem-solving, and asks occasional questions to better understand their thinking.',
            'level-4':
              'Has learners lead problem-solving, and regularly asks effective questions to understand their thinking. Rotates spotlight to ensure all learners have a chance to share their ideas.',
            'level-0':
              'Atypical session format, such as a test-taking session where no problems are reviewed',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-3',
          type: 'level',
          required: true,
          label: `Builds on learners' ideas effectively during “we do” strategy`,
          description:
            'Does the tutor effectively build on learner ideas and turn mistakes into learning opportunities?',
          responseDescriptions: {
            'level-1': 'Does not ask learners to take the lead in solving any problems.',
            'level-2':
              'When a learner is unsure, stuck or incorrect, they tend to jump in and explain things in their own way.',
            'level-3': `Encourages learners to share their ideas, even when they're incorrect, and builds directly on those ideas to guide and strengthen their understanding.`,
            'level-4': `Encourages learners to share their ideas, even when they're incorrect, and builds directly on those ideas to guide and strengthen their understanding. Skillfully turns learners' mistakes into opportunities to better understand the topic.`,
            'level-0':
              'Atypical session format, such as a test-taking session where no problems are reviewed',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Please justify your ratings above—what worked well? What could be improved? This will be shared with the tutor.',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
        {
          slug: 'active-comment-2',
          type: 'short-answer',
          required: true,
          label: 'Is there any evidence that the learners made progress?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Respect
    {
      slug: 'respect',
      title: 'Respect',
      questions: [
        {
          slug: 'respect-1',
          type: 'level',
          required: true,
          label: 'Creates a comfortable learning environment',
          description:
            'Does the tutor make an effort to get to know learners, and chat with them before sessions? Do they show sensitivity towards differences across the group, such as timezones, language experience, cultural background, or connectivity difficulties?',
          responseDescriptions: {
            'level-1': `Waits for everyone to join the session without welcoming anyone or making conversation. May also show insensitivity towards a learners' background or situation.`,
            'level-2': `Greets learners as they join, but doesn't facilitate conversation or refer to each learner by name throughout the session. May need some support to know how to be sensitive with learners (e.g., assuming name pronunciations and getting them wrong).`,
            'level-3': `Shows respect towards all learner(s)—chats with them about how they're doing, refers to them by name throughout the session, and presents themselves as a peer who cares about their success.`,
            'level-4':
              'Facilitates a deeply friendly and respectful environment, where tutor and learner(s) refer to each other by name, show their personality, and feel a sense of community.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'respect-comment-1',
          type: 'short-answer',
          required: true,
          label: 'Please justify your ratings above—what worked well? What could be improved?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Safety
    {
      slug: 'safety',
      title: 'Safe & Appropriate Environment',
      questions: [
        {
          slug: SAFETY_REPORT_SLUG,
          type: 'yes-no',
          required: true,
          label: 'Keeps interactions safe and appropriate',
          description: `Does the tutor facilitate a safe environment for learners, in accordance with Schoolhouse's community guidelines? Were there any interactions that seemed concerning?`,
          responseDescriptions: {
            yes: 'All interactions were safe and appropriate.',
            no: 'Something about the session was questionable, and should be escalated to the safety team.',
          },
          defaultValue: 'yes',
        },
      ],
    },
    // Internal Escalation
    {
      slug: 'internal',
      title: 'Session Escalation',
      questions: [
        {
          slug: COACH_ESCALATION_SLUG,
          type: 'yes-no',
          required: true,
          label: 'Escalate this session to a coach for further review?',
          description:
            'Do you think the feedback you provided should be enough to help the tutor, or would it be best for there to be more intervention?',
          responseDescriptions: {
            yes: 'There was at least one issue in this session that I think the tutor needs additional support to work through.',
            no: 'The feedback I shared should be enough to help the tutor improve next time.',
          },
          defaultValue: DEFAULT_YES_NO,
        },
        {
          slug: INTERNAL_COMMENT_SLUG,
          type: 'short-answer',
          required: false,
          label:
            "It looks like you've escalated this session because of safety or quality concerns. " +
            'Please share your specific concerns so our team can look into them further—this note will not be shared with the tutor!',
          sublabel: 'Only coaches and safety team members will be able to see your comment.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
  ],
}

const AUDIT_RUBRIC_MARS_SAT_V2: AuditRubricQuestions = {
  version: 'mars-sat-v2',
  blocks: [
    // Mastery
    {
      slug: 'mastery',
      title: 'Mastery',
      questions: [
        {
          slug: 'mastery-1',
          type: 'level',
          required: true,
          label: 'Conveys a deep understanding of material',
          description:
            'Is the tutor prepared to answer questions in a clear and thorough way? Do they share strategies for efficiently answering similar questions during testing day?',
          responseDescriptions: {
            'level-1': 'Gets multiple questions wrong, gives out disinformation.',
            'level-2':
              'Tutor struggles to explain material, perhaps failing to review the material beforehand. However, explanations are mostly correct.',
            'level-3':
              'Explains the process to answer a question correctly, in a reasonable way. They also help learners understand how to approach the questions during a real test. ',
            'level-4':
              'Explains the process to answer a question correctly, in a very clear and thorough way. They also share excellent strategies for facing similar questions during a real test.',
            'level-0': 'No SAT content was reviewed during the session.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'mastery-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Consider the explanations, visuals, analogies and examples that the tutor used to cover the content of this session—what worked well? What could be improved?',
          sublabel:
            'Please focus on content-specific examples! This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Active Learning
    {
      slug: 'active',
      title: 'Active Learning',
      questions: [
        {
          slug: 'active-1',
          type: 'level',
          required: true,
          label: `Checks learners' prior knowledge`,
          description:
            'Does the tutor assume that the learner knows all prerequisite terms and concepts, or do they check for knowledge gaps?',
          responseDescriptions: {
            'level-1':
              'Always assumes that learners have prerequisite knowledge to solve a problem.',
            'level-2': `Does not check learners' pre-requisite knowledge, but explains prerequisite concepts when a learner gets a question wrong.`,
            'level-3': `Regularly checks learners' prerequisite knowledge, and takes time to explain any concepts learners are not already familiar with.`,
            'level-4': `Excellent job checking learners' prerequisite knowledge, filling in knowledge gaps, and adjusting the pace of the session accordingly.`,
            'level-0': 'No SAT content was reviewed during the session.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-2',
          type: 'level',
          required: true,
          label: `Adapted to learners' unique needs`,
          description:
            'Does the tutor consistently notice when a learner was not following, and take the time to understand why? ',
          responseDescriptions: {
            'level-1':
              'Generally does not adapt their tutoring based on how learners respond. When learners get questions wrong, they may repeat their original explanation of the concept but continue to power through.',
            'level-2': `Asks learners questions to see if they're following, but most questions are not very effective (e.g., “does that make sense”?). For most of the session, it's unclear whether the learners are following along.`,
            'level-3':
              'Asks some effective questions to check which learners are following along, has learner(s) explain their reasoning, and works to find explanations that resonate with learners. However, there are still some points in the session where the tutor struggles to be able to adapt to learner(s) needs.',
            'level-4': `Engages learners in a way that makes it very clear when they're not following along. Builds off of learners' existing knowledge in a highly successful way.`,
            'level-0': `Session is very large or, for some other reason, it doesn't make sense for it to be adaptive`,
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-3',
          type: 'level',
          required: true,
          label: 'Continuously engages learners',
          description:
            'Is the tutor able to effectively engage learners as active participants, and keep their attention? Is the tutor speaking less than the learner(s) are?',
          responseDescriptions: {
            'level-1':
              'Only engages learners by requesting answers to questions (e.g., a or b, yes or no)',
            'level-2':
              'Provides occasional chances for learners to engage with material, ask questions, or solve problems. However, nearly all of the engagement is coming from the tutor.',
            'level-3':
              'Frequently engages learners in solving problems and discussing the content. However, most of the engagement and discussion still comes from the tutor.',
            'level-4':
              'Successfully engages learners as leaders of their learning process—asking questions, solving problems, and talking with each other. A large portion of the engagement in this session comes from the learners.',
            'level-0':
              'Atypical session format, such as a test-taking session where no problems are reviewed',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-4',
          type: 'level',
          required: true,
          label: `Confirms learners' progress`,
          description: 'Does the tutor check in to ensure all learners are following along?',
          responseDescriptions: {
            'level-1': 'Does not ask learners if they were following along.',
            'level-2':
              'Occasionally asks learners if something made sense, or asks if they have questions.',
            'level-3':
              'Regularly asks learners if something made sense, or asks if they have questions.',
            'level-4': `Regularly checks in to see how confident everyone is about a concept they're reviewing, in a way that is comfortable for learners to honestly respond to.`,
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'active-comment-1',
          type: 'short-answer',
          required: true,
          label:
            'Please justify your ratings above—what worked well? What could be improved? This will be shared with the tutor.',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
        {
          slug: 'active-comment-2',
          type: 'short-answer',
          required: true,
          label: 'Is there any evidence that the learners made progress?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Respect
    {
      slug: 'respect',
      title: 'Respect',
      questions: [
        {
          required: true,
          type: 'level',
          slug: 'respect-1',
          label: `Strengthens learners' confidence`,
          description: `Does the tutor subtly strengthen the learner's belief that they're capable of doing well? Do they normalize that the test is hard for everyone, and that they don't need to get every question right to get a great score?`,
          responseDescriptions: {
            'level-1':
              'Grows frustrated when learners make mistakes, or describes something as “easy” which a learner may find hard.',
            'level-2': `Tries to be patient when the learner makes mistakes, but isn't able to guide them through in a way that avoids discouragement. Attempts to encourage learners, but it doesn't always feel believable.`,
            'level-3': `Able to skillfully navigate learner's mistakes, guiding them in the right direction and keeping them focused on progress. Normalizes that the test is hard, and that it takes work to succeed.`,
            'level-4':
              'Builds an environment where learners are very comfortable making mistakes and learning from them. The tutor is tuned in to how the learners are feeling and finds small but effective ways to develop their belief that they can do well on the test.',
            'level-0': 'Tutor did not engage learners in practice problems',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'respect-2',
          type: 'level',
          required: true,
          label: 'Creates a comfortable learning environment',
          description:
            'Does the tutor make an effort to get to know learners, and chat with them before sessions? Do they show sensitivity towards differences across the group, such as timezones, language experience, cultural background, or connectivity difficulties?',
          responseDescriptions: {
            'level-1': `Waits for everyone to join the session without welcoming anyone or making conversation. May also show insensitivity towards a learners' background or situation.`,
            'level-2': `Greets learners as they join, but doesn't facilitate conversation or refer to each learner by name throughout the session. May need some support to know how to be sensitive with learners (e.g., assuming name pronunciations and getting them wrong).`,
            'level-3': `Shows respect towards all learner(s)—chats with them about how they'd doing, refers to them by name throughout the session, and presents themselves as a peer who cares about their success.`,
            'level-4':
              'Facilitates a deeply friendly and respectful environment, where tutor and learner(s) refer to each other by name, show their personality, and feel a sense of community.',
            'level-0': 'Atypical session format, such as a test-taking session',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          slug: 'respect-comment-1',
          type: 'short-answer',
          required: true,
          label: 'Please justify your ratings above—what worked well? What could be improved?',
          sublabel: 'This will be shared with the tutor.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Safety
    {
      slug: 'safety',
      title: 'Safe & Appropriate Environment',
      questions: [
        {
          required: true,
          type: 'yes-no',
          slug: SAFETY_REPORT_SLUG,
          label: 'Keeps interactions safe and appropriate',
          description: `Does the tutor facilitate a safe environment for learners, in accordance with Schoolhouse's community guidelines? Were there any interactions that seemed concerning?`,
          responseDescriptions: {
            yes: 'All interactions were safe and appropriate.',
            no: 'Something about the session was questionable, and should be escalated to the safety team.',
          },
          defaultValue: 'yes',
        },
        {
          required: true,
          type: 'yes-no',
          slug: 'safety-2',
          label: `Never completes the learner's homework for them`,
          description: '',
          responseDescriptions: {
            yes: 'The tutor guided the learner, but the learner did all the thinking and work.',
            no: 'The tutor solved problems or completed steps for the learner.',
          },
          defaultValue: 'yes',
        },
      ],
    },
    // Internal Escalation
    {
      slug: 'internal',
      title: 'Session Escalation',
      questions: [
        {
          required: true,
          type: 'yes-no',
          slug: COACH_ESCALATION_SLUG,
          label: 'Escalate this session to a coach for further review?',
          description:
            'Do you think the feedback you provided should be enough to help the tutor, or would it be best for there to be more intervention?',
          responseDescriptions: {
            yes: 'There was at least one issue in this session that I think the tutor needs additional support to work through.',
            no: 'The feedback I shared should be enough to help the tutor improve next time.',
          },
          defaultValue: DEFAULT_YES_NO,
        },
        {
          required: false,
          type: 'short-answer',
          slug: INTERNAL_COMMENT_SLUG,
          label:
            "It looks like you've escalated this session because of safety or quality concerns. " +
            'Please share your specific concerns so our team can look into them further—this note will not be shared with the tutor!',
          sublabel: 'Only coaches and safety team members will be able to see your comment.',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
  ],
}

const AUDIT_RUBRIC_MARS_SELF_SAT_V2: AuditRubricQuestions = {
  version: 'mars-self-sat-v2',
  blocks: [
    // Mastery
    {
      slug: 'mastery',
      title: 'Mastery',
      questions: [
        {
          required: true,
          type: 'level',
          slug: 'mastery-1',
          label: 'Clear explanations',
          description:
            'Was I prepared to answer questions in a clear and thorough way? Did I share strategies for efficiently solving similar problems during testing day?',
          responseDescriptions: {
            'level-1':
              'I came to the wrong conclusion on multiple practice problems, or realized after the session that some explanations were inaccurate.',
            'level-2':
              'I struggled to explain material, and may have benefitted from reviewing it more beforehand. However, the explanations I did share were mostly correct.',
            'level-3':
              'I explained the process to answer a question correctly, in a reasonable way. I also helped learners understand how to approach the questions during a real test. ',
            'level-4':
              'I explained the process to answer each question correctly, in a clear and thorough way. I also shared strategies for facing similar questions during a real test.',
            'level-0': 'Not applicable.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'short-answer',
          slug: 'mastery-comment-1',
          label:
            'Consider your explanations of different test questions and the surrounding concepts. What seemed to resonate well with learners? Is there anything you want to improve next time?',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Active Learning
    {
      slug: 'active',
      title: 'Active Learning',
      questions: [
        {
          required: true,
          type: 'level',
          slug: 'active-2',
          label: `Adapted to learners' unique needs`,
          description:
            'Did I consistently notice when a learner was not following, and take the time to understand why? Was I able to help fill in their knowledge gaps?',
          responseDescriptions: {
            'level-1': `I mostly taught based on what I'd planned, and didn't get a sense of whether it was meeting learners' specific needs.`,
            'level-2':
              'I asked learners questions to see if they were following along, but struggled to get a meaningful response. For most of the session, it was unclear whether the learners were following along.',
            'level-3':
              'I asked questions to check if learners were following along, had learners explain their reasoning, and worked to find explanations that resonated. However, I still struggled to cater to the needs of everyone in the session.',
            'level-4':
              'I was able to tease apart the individual learning needs of everyone in the session, and build on what they knew in a successful way.',
            'level-0': 'Not applicable.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'level',
          slug: 'active-3',
          label: 'Kept learners engaged',
          description:
            'Was I able to effectively engage all learners as active participants, and keep their attention?',
          responseDescriptions: {
            'level-1':
              'I only engaged learners by requesting answers to questions (e.g., a or b, yes or no).',
            'level-2':
              'I provided occasional chances for learners to engage with material, ask questions, or solve problems. However, nearly all of the engagement still came from me.',
            'level-3':
              'I frequently engaged learners in solving problems and discussing the content. However, most of the engagement and discussion still came from me.',
            'level-4':
              'I was able to engage learners as leaders of their learning process—asking questions, solving problems, and talking with each other. A significant portion of the engagement in this session came from learners.',
            'level-0': 'Not applicable.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'level',
          slug: 'active-4',
          label: `Confirmed learners' progress`,
          description: 'Did I check in to ensure all learners were following along?',
          responseDescriptions: {
            'level-1': 'I did not ask learners questions to check if they were following along.',
            'level-2':
              'I occasionally asked learners questions to check if they were following along.',
            'level-3':
              'I regularly asked learners questions to check if they were following along.',
            'level-4':
              'I regularly checked in to see how confident everyone was in each concept we reviewed, and learners were comfortable enough to admit when they were confused.',
            'level-0': 'Not applicable.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'short-answer',
          slug: 'active-comment-1',
          label:
            'Reflecting on how you encouraged active learning in your session, what do you feel went well? What do you want to do differently next time?',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
        {
          required: true,
          type: 'short-answer',
          slug: 'active-comment-2',
          label:
            'Which learners did you feel made the most progress? Are there any learners who you want to engage more in your next session?',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Respect
    {
      slug: 'respect',
      title: 'Respect',
      questions: [
        {
          required: true,
          type: 'level',
          slug: 'respect-1',
          label: `Strengthened learners' confidence`,
          description: `Did I help build my learners' confidence that they're capable of doing well? Did I normalize that the test is hard for everyone, and that they don't need to get every question right to get a great score?`,
          responseDescriptions: {
            'level-1':
              'I found myself frustrated when learners made mistakes, and struggled to respond in a constructive way.',
            'level-2': `I tried to be patient when learners made mistakes, but still struggled to respond in a constructive way. I attempted to encourage learners, but I'm not sure if it resonated.`,
            'level-3': `I was able to effectively navigate learner's mistakes, guiding them in the right direction and keeping them focused on progress. I normalized that the test is hard, and that it takes work to succeed.`,
            'level-4':
              'I built an environment where learners were very comfortable making mistakes and learning from them. I was tuned in to how the learners were feeling and found opportunities to develop their belief that they can do well on the test.',
            'level-0': 'Not applicable.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'level',
          slug: 'respect-2',
          label: 'Created a comfortable learning environment',
          description:
            'Did I make an effort to get to know learners, and chat with them before sessions? Did I show sensitivity towards differences across the group, such as timezones, language experience, cultural background, or connectivity difficulties?',
          responseDescriptions: {
            'level-1': `I waited for everyone to join the session without welcoming anyone or making conversation. I was not making an effort to be sensitive to my learners' unique backgrounds and contexts.`,
            'level-2': `I greeted learners as they joined, but didn't facilitate conversation or refer to each learner by name throughout the session. I struggled to support sensitivities with learners (e.g., assuming name pronunciations and getting them wrong).`,
            'level-3':
              'I feel that I was able to show respect towards all learner(s)—chatted with them as the session got started, referred to them by name throughout the session, and presented myself as a peer who cares about their success.',
            'level-4':
              'I facilitated a deeply friendly and respectful environment, where everyone referred to each other by name, showed their personality, and felt a sense of community.',
            'level-0': 'Not applicable.',
          },
          defaultValue: DEFAULT_LEVEL,
        },
        {
          required: true,
          type: 'short-answer',
          slug: 'respect-comment-1',
          label:
            'What worked well to build a comfortable environment? What do you hope to improve next time?',
          minLength: ANSWER_MIN_LENGTH,
          defaultValue: DEFAULT_SHORT_ANSWER,
        },
      ],
    },
    // Safety
    {
      slug: 'safety',
      title: 'Safety',
      questions: [
        {
          required: true,
          type: 'yes-no',
          slug: SAFETY_REPORT_SLUG,
          label: 'Respectful, safe and appropriate interactions',
          description: `Was your session a safe and appropriate environment, in accordance with Schoolhouse's community guidelines? Were there any interactions that seemed concerning?`,
          responseDescriptions: {
            yes: 'All interactions were safe and appropriate.',
            no: 'At least one interaction was questionable or uncomfortable, and should be escalated to the safety team.',
          },
          defaultValue: 'yes',
        },
      ],
    },
  ],
}

export const AUDIT_RUBRICS = {
  'lead-v1': AUDIT_RUBRIC_LEAD_V1,
  'mars-v2': AUDIT_RUBRIC_MARS_V2,
  'mars-sat-v3': AUDIT_RUBRIC_MARS_SAT_V3,
  'mars-sat-v2': AUDIT_RUBRIC_MARS_SAT_V2,
  'mars-self-sat-v2': AUDIT_RUBRIC_MARS_SELF_SAT_V2,
} satisfies Record<AuditRubricVersion, AuditRubricQuestions>

// We must explicitly list the schemas here to prove that the array
// of schemas that we pass to z.discriminatedUnion is non-empty.
export const AUDIT_RUBRIC_SCHEMAS = [
  auditRubricToSchema(AUDIT_RUBRIC_LEAD_V1),
  auditRubricToSchema(AUDIT_RUBRIC_MARS_V2),
  auditRubricToSchema(AUDIT_RUBRIC_MARS_SAT_V3),
  auditRubricToSchema(AUDIT_RUBRIC_MARS_SAT_V2),
  auditRubricToSchema(AUDIT_RUBRIC_MARS_SELF_SAT_V2),
] as const
